// slices/providerImageSlice.js
import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import ServiceProviderImageService from "../services/ServiceProviderImageService";

// ==================== HELPER: Error Handler ====================
const handleError = (error) => {
  console.log('Full error object:', JSON.stringify(error, null, 2));
  
  let errorMessage = 'An unexpected error occurred. Please try again.';
  
  if (typeof error === 'string') {
    errorMessage = error;
  } else if (error?.data?.error?.message) {
    errorMessage = error.data.error.message;
  } else if (error?.error && typeof error.error === 'string') {
    errorMessage = error.error;
  } else if (error?.message) {
    errorMessage = error.message;
  } else if (error?.response?.data?.error?.message) {
    errorMessage = error.response.data.error.message;
  } else if (error?.response?.data?.error && typeof error.response.data.error === 'string') {
    errorMessage = error.response.data.error;
  } else if (error?.response?.data?.message) {
    errorMessage = error.response.data.message;
  } else if (error?.response?.data?.data?.error?.message) {
    errorMessage = error.response.data.data.error.message;
  } else if (error?.success === false && error?.error) {
    errorMessage = error.error;
  } else if (error?.data && error?.statusCode) {
    if (error.data.error?.message) {
      errorMessage = error.data.error.message;
    } else if (error.data.message) {
      errorMessage = error.data.message;
    }
  } else if (error?.response) {
    if (error.response.data?.error?.message) {
      errorMessage = error.response.data.error.message;
    } else if (error.response.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response.statusText) {
      errorMessage = error.response.statusText;
    }
  } else if (error?.error) {
    if (typeof error.error === 'string') {
      errorMessage = error.error;
    } else if (error.error.message) {
      errorMessage = error.error.message;
    }
  }
  
  console.log('Extracted error message:', errorMessage);
  return errorMessage;
};

// ==================== HELPER: Update user data in localStorage ====================
const updateUserDataInStorage = (updates) => {
  try {
    const userDataStr = localStorage.getItem('user_data');
    if (!userDataStr) return null;
    
    const userData = JSON.parse(userDataStr);
    const updatedUserData = {
      ...userData,
      providerProfile: {
        ...(userData.providerProfile || {}),
        ...updates
      }
    };
    
    localStorage.setItem('user_data', JSON.stringify(updatedUserData));
    console.log('✅ User data updated in localStorage:', updates);
    return updatedUserData;
  } catch (error) {
    console.error('❌ Failed to update user data in localStorage:', error);
    return null;
  }
};

// ==================== ASYNC THUNKS ====================

// -------- PROFILE IMAGE --------

/**
 * Upload profile image (requires auth)
 * POST /api/service-providers/:providerId/images/profile
 */
export const uploadProfileImage = createAsyncThunk(
  'providerImage/uploadProfileImage',
  async ({ providerId, file, options = {} }, { rejectWithValue }) => {
    try {
      const response = await ServiceProviderImageService.uploadProfileImage(
        providerId,
        file,
        options
      );
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get provider profile image (public)
 * GET /api/service-providers/:providerId/images/profile
 */
export const getProviderProfileImage = createAsyncThunk(
  'providerImage/getProviderProfileImage',
  async (providerId, { rejectWithValue }) => {
    try {
      const response = await ServiceProviderImageService.getProviderProfileImage(providerId);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Remove profile image (requires auth)
 * DELETE /api/service-providers/:providerId/images/profile
 */
export const removeProfileImage = createAsyncThunk(
  'providerImage/removeProfileImage',
  async (providerId, { rejectWithValue }) => {
    try {
      const response = await ServiceProviderImageService.removeProfileImage(providerId);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// -------- COVER IMAGE --------

/**
 * Upload cover image (requires auth)
 * POST /api/service-providers/:providerId/images/cover
 */
export const uploadCoverImage = createAsyncThunk(
  'providerImage/uploadCoverImage',
  async ({ providerId, file, options = {} }, { rejectWithValue }) => {
    try {
      const response = await ServiceProviderImageService.uploadCoverImage(
        providerId,
        file,
        options
      );
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get provider cover image (public)
 * GET /api/service-providers/:providerId/images/cover
 */
export const getProviderCoverImage = createAsyncThunk(
  'providerImage/getProviderCoverImage',
  async (providerId, { rejectWithValue }) => {
    try {
      const response = await ServiceProviderImageService.getProviderCoverImage(providerId);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Remove cover image (requires auth)
 * DELETE /api/service-providers/:providerId/images/cover
 */
export const removeCoverImage = createAsyncThunk(
  'providerImage/removeCoverImage',
  async (providerId, { rejectWithValue }) => {
    try {
      const response = await ServiceProviderImageService.removeCoverImage(providerId);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// -------- IMAGES SUMMARY --------

/**
 * Get provider images summary (profile + cover) (public)
 * GET /api/service-providers/:providerId/images/summary
 */
export const getProviderImagesSummary = createAsyncThunk(
  'providerImage/getProviderImagesSummary',
  async (providerId, { rejectWithValue }) => {
    try {
      const response = await ServiceProviderImageService.getProviderImagesSummary(providerId);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// -------- OPTIMIZED IMAGES --------

/**
 * Get optimized image URL (public)
 * GET /api/service-providers/images/optimized/:publicId
 */
export const getOptimizedImageUrl = createAsyncThunk(
  'providerImage/getOptimizedImageUrl',
  async ({ publicId, options = {} }, { rejectWithValue }) => {
    try {
      const response = await ServiceProviderImageService.getOptimizedImageUrl(
        publicId,
        options
      );
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// -------- DELETE ALL IMAGES --------

/**
 * Delete all images associated with a provider (admin only)
 * DELETE /api/service-providers/:providerId/images/all
 */
export const deleteAllProviderImages = createAsyncThunk(
  'providerImage/deleteAllProviderImages',
  async (providerId, { rejectWithValue }) => {
    try {
      const response = await ServiceProviderImageService.deleteAllProviderImages(providerId);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// ==================== SLICE ====================

const initialState = {
  // Profile image
  profileImage: null,
  
  // Cover image
  coverImage: null,
  
  // Optimized image URL
  optimizedImageUrl: null,
  
  // Images summary
  imagesSummary: null,
  
  // Upload progress
  uploadProgress: 0,
  
  // Loading states
  loading: false,
  uploading: false,
  deleting: false,
  fetchingProfileImage: false,
  fetchingCoverImage: false,
  fetchingSummary: false,
  
  // Error states
  error: null,
  uploadError: null,
  deleteError: null,
  summaryError: null,
  
  // Success states
  uploadSuccess: false,
  deleteSuccess: false,
  summarySuccess: false,
  
  // UI states
  isRefreshing: false,
  
  // Image counts
  imageCounts: {
    profile: 0,
    cover: 0,
    total: 0
  }
};

const providerImageSlice = createSlice({
  name: 'providerImage',
  initialState,
  reducers: {
    // Reset states
    resetImageState: (state) => {
      state.profileImage = null;
      state.coverImage = null;
      state.optimizedImageUrl = null;
      state.imagesSummary = null;
      state.error = null;
      state.uploadError = null;
      state.deleteError = null;
      state.summaryError = null;
      state.uploadSuccess = false;
      state.deleteSuccess = false;
      state.summarySuccess = false;
      state.uploadProgress = 0;
      state.imageCounts = {
        profile: 0,
        cover: 0,
        total: 0
      };
    },
    
    // Reset upload states
    resetUploadState: (state) => {
      state.uploadSuccess = false;
      state.uploadError = null;
      state.uploadProgress = 0;
    },
    
    // Reset delete states
    resetDeleteState: (state) => {
      state.deleteSuccess = false;
      state.deleteError = null;
    },
    
    // Reset summary states
    resetSummaryState: (state) => {
      state.summarySuccess = false;
      state.summaryError = null;
      state.imagesSummary = null;
    },
    
    // Clear errors
    clearImageErrors: (state) => {
      state.error = null;
      state.uploadError = null;
      state.deleteError = null;
      state.summaryError = null;
    },
    
    // Set upload progress
    setUploadProgress: (state, action) => {
      state.uploadProgress = action.payload;
    },
    
    // Set refreshing
    setImageRefreshing: (state, action) => {
      state.isRefreshing = action.payload;
    },
    
    // Clear profile image
    clearProfileImage: (state) => {
      state.profileImage = null;
      state.imageCounts.profile = 0;
      state.imageCounts.total = Math.max(0, state.imageCounts.total - 1);
      updateUserDataInStorage({ profileImage: null });
    },
    
    // Clear cover image
    clearCoverImage: (state) => {
      state.coverImage = null;
      state.imageCounts.cover = 0;
      state.imageCounts.total = Math.max(0, state.imageCounts.total - 1);
      updateUserDataInStorage({ coverImage: null });
    },
    
    // Update image counts
    updateImageCounts: (state) => {
      let total = 0;
      if (state.profileImage) total++;
      if (state.coverImage) total++;
      state.imageCounts.profile = state.profileImage ? 1 : 0;
      state.imageCounts.cover = state.coverImage ? 1 : 0;
      state.imageCounts.total = total;
    }
  },
  extraReducers: (builder) => {
    builder
      // ========== UPLOAD PROFILE IMAGE ==========
      .addCase(uploadProfileImage.pending, (state) => {
        state.uploading = true;
        state.uploadError = null;
        state.uploadSuccess = false;
        state.uploadProgress = 0;
      })
      .addCase(uploadProfileImage.fulfilled, (state, action) => {
        state.uploading = false;
        state.uploadSuccess = true;
        state.uploadProgress = 100;
        
        const providerImage = action.payload?.data?.provider || action.payload?.data || action.payload;
        state.profileImage = providerImage;
        state.imageCounts.profile = 1;
        state.imageCounts.total = state.imageCounts.total + 1;
        
        if (providerImage) {
          updateUserDataInStorage({
            profileImage: {
              url: providerImage.url,
              publicId: providerImage.publicId,
              thumbnail: providerImage.thumbnail || null
            }
          });
        }
      })
      .addCase(uploadProfileImage.rejected, (state, action) => {
        state.uploading = false;
        state.uploadError = action.payload || 'Failed to upload profile image';
        state.uploadSuccess = false;
        state.uploadProgress = 0;
      })
      
      // ========== GET PROVIDER PROFILE IMAGE ==========
      .addCase(getProviderProfileImage.pending, (state) => {
        state.fetchingProfileImage = true;
        state.error = null;
      })
      .addCase(getProviderProfileImage.fulfilled, (state, action) => {
        state.fetchingProfileImage = false;
        state.profileImage = action.payload?.data || null;
        state.imageCounts.profile = state.profileImage ? 1 : 0;
        state.imageCounts.total = (state.imageCounts.profile + state.imageCounts.cover);
      })
      .addCase(getProviderProfileImage.rejected, (state, action) => {
        state.fetchingProfileImage = false;
        state.error = action.payload || 'Failed to get profile image';
      })
      
      // ========== REMOVE PROFILE IMAGE ==========
      .addCase(removeProfileImage.pending, (state) => {
        state.deleting = true;
        state.deleteError = null;
        state.deleteSuccess = false;
      })
      .addCase(removeProfileImage.fulfilled, (state) => {
        state.deleting = false;
        state.deleteSuccess = true;
        state.profileImage = null;
        state.imageCounts.profile = 0;
        state.imageCounts.total = state.imageCounts.cover;
        updateUserDataInStorage({ profileImage: null });
      })
      .addCase(removeProfileImage.rejected, (state, action) => {
        state.deleting = false;
        state.deleteError = action.payload || 'Failed to remove profile image';
        state.deleteSuccess = false;
      })
      
      // ========== UPLOAD COVER IMAGE ==========
      .addCase(uploadCoverImage.pending, (state) => {
        state.uploading = true;
        state.uploadError = null;
        state.uploadSuccess = false;
        state.uploadProgress = 0;
      })
      .addCase(uploadCoverImage.fulfilled, (state, action) => {
        state.uploading = false;
        state.uploadSuccess = true;
        state.uploadProgress = 100;
        
        const coverImage = action.payload?.data || action.payload;
        state.coverImage = coverImage;
        state.imageCounts.cover = 1;
        state.imageCounts.total = state.imageCounts.total + 1;
        
        if (coverImage) {
          updateUserDataInStorage({
            coverImage: {
              url: coverImage.url,
              publicId: coverImage.publicId
            }
          });
        }
      })
      .addCase(uploadCoverImage.rejected, (state, action) => {
        state.uploading = false;
        state.uploadError = action.payload || 'Failed to upload cover image';
        state.uploadSuccess = false;
        state.uploadProgress = 0;
      })
      
      // ========== GET PROVIDER COVER IMAGE ==========
      .addCase(getProviderCoverImage.pending, (state) => {
        state.fetchingCoverImage = true;
        state.error = null;
      })
      .addCase(getProviderCoverImage.fulfilled, (state, action) => {
        state.fetchingCoverImage = false;
        state.coverImage = action.payload?.data || null;
        state.imageCounts.cover = state.coverImage ? 1 : 0;
        state.imageCounts.total = (state.imageCounts.profile + state.imageCounts.cover);
      })
      .addCase(getProviderCoverImage.rejected, (state, action) => {
        state.fetchingCoverImage = false;
        state.error = action.payload || 'Failed to get cover image';
      })
      
      // ========== REMOVE COVER IMAGE ==========
      .addCase(removeCoverImage.pending, (state) => {
        state.deleting = true;
        state.deleteError = null;
        state.deleteSuccess = false;
      })
      .addCase(removeCoverImage.fulfilled, (state) => {
        state.deleting = false;
        state.deleteSuccess = true;
        state.coverImage = null;
        state.imageCounts.cover = 0;
        state.imageCounts.total = state.imageCounts.profile;
        updateUserDataInStorage({ coverImage: null });
      })
      .addCase(removeCoverImage.rejected, (state, action) => {
        state.deleting = false;
        state.deleteError = action.payload || 'Failed to remove cover image';
        state.deleteSuccess = false;
      })
      
      // ========== GET PROVIDER IMAGES SUMMARY ==========
      .addCase(getProviderImagesSummary.pending, (state) => {
        state.fetchingSummary = true;
        state.summaryError = null;
        state.summarySuccess = false;
      })
      .addCase(getProviderImagesSummary.fulfilled, (state, action) => {
        state.fetchingSummary = false;
        state.summarySuccess = true;
        state.imagesSummary = action.payload?.data || null;
        if (action.payload?.data) {
          state.imageCounts = {
            profile: action.payload.data.profileImage ? 1 : 0,
            cover: action.payload.data.coverImage ? 1 : 0,
            total: action.payload.data.totalImages || 0
          };
        }
      })
      .addCase(getProviderImagesSummary.rejected, (state, action) => {
        state.fetchingSummary = false;
        state.summaryError = action.payload || 'Failed to get provider images summary';
        state.summarySuccess = false;
      })
      
      // ========== GET OPTIMIZED IMAGE URL ==========
      .addCase(getOptimizedImageUrl.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getOptimizedImageUrl.fulfilled, (state, action) => {
        state.loading = false;
        state.optimizedImageUrl = action.payload?.data?.url || null;
      })
      .addCase(getOptimizedImageUrl.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to get optimized image URL';
      })
      
      // ========== DELETE ALL PROVIDER IMAGES ==========
      .addCase(deleteAllProviderImages.pending, (state) => {
        state.deleting = true;
        state.deleteError = null;
        state.deleteSuccess = false;
      })
      .addCase(deleteAllProviderImages.fulfilled, (state) => {
        state.deleting = false;
        state.deleteSuccess = true;
        state.profileImage = null;
        state.coverImage = null;
        state.imagesSummary = null;
        state.imageCounts = {
          profile: 0,
          cover: 0,
          total: 0
        };
        updateUserDataInStorage({ profileImage: null, coverImage: null });
      })
      .addCase(deleteAllProviderImages.rejected, (state, action) => {
        state.deleting = false;
        state.deleteError = action.payload || 'Failed to delete all provider images';
        state.deleteSuccess = false;
      });
  }
});

// ==================== SELECTORS ====================

// Base selector
const selectImageState = (state) => state.providerImage;

// Profile image selectors
export const selectProfileImage = createSelector(
  selectImageState,
  (image) => image.profileImage
);

export const selectProfileImageUrl = createSelector(
  selectProfileImage,
  (profileImage) => profileImage?.url || null
);

export const selectProfileImageThumbnail = createSelector(
  selectProfileImage,
  (profileImage) => profileImage?.thumbnail || null
);

export const selectHasProfileImage = createSelector(
  selectProfileImage,
  (profileImage) => profileImage !== null && profileImage !== undefined
);

export const selectProfileImagePublicId = createSelector(
  selectProfileImage,
  (profileImage) => profileImage?.publicId || null
);

// Cover image selectors
export const selectCoverImage = createSelector(
  selectImageState,
  (image) => image.coverImage
);

export const selectCoverImageUrl = createSelector(
  selectCoverImage,
  (coverImage) => coverImage?.url || null
);

export const selectHasCoverImage = createSelector(
  selectCoverImage,
  (coverImage) => coverImage !== null && coverImage !== undefined
);

export const selectCoverImagePublicId = createSelector(
  selectCoverImage,
  (coverImage) => coverImage?.publicId || null
);

// Images summary selectors
export const selectImagesSummary = createSelector(
  selectImageState,
  (image) => image.imagesSummary
);

// Optimized image URL selectors
export const selectOptimizedImageUrl = createSelector(
  selectImageState,
  (image) => image.optimizedImageUrl
);

// Loading selectors
export const selectImageLoading = createSelector(
  selectImageState,
  (image) => image.loading
);

export const selectImageUploading = createSelector(
  selectImageState,
  (image) => image.uploading
);

export const selectImageDeleting = createSelector(
  selectImageState,
  (image) => image.deleting
);

export const selectUploadProgress = createSelector(
  selectImageState,
  (image) => image.uploadProgress
);

export const selectFetchingProfileImage = createSelector(
  selectImageState,
  (image) => image.fetchingProfileImage
);

export const selectFetchingCoverImage = createSelector(
  selectImageState,
  (image) => image.fetchingCoverImage
);

export const selectFetchingSummary = createSelector(
  selectImageState,
  (image) => image.fetchingSummary
);

// Error selectors
export const selectImageError = createSelector(
  selectImageState,
  (image) => image.error
);

export const selectUploadError = createSelector(
  selectImageState,
  (image) => image.uploadError
);

export const selectDeleteError = createSelector(
  selectImageState,
  (image) => image.deleteError
);

export const selectSummaryError = createSelector(
  selectImageState,
  (image) => image.summaryError
);

// Success selectors
export const selectUploadSuccess = createSelector(
  selectImageState,
  (image) => image.uploadSuccess
);

export const selectDeleteSuccess = createSelector(
  selectImageState,
  (image) => image.deleteSuccess
);

export const selectSummarySuccess = createSelector(
  selectImageState,
  (image) => image.summarySuccess
);

// UI selectors
export const selectIsRefreshing = createSelector(
  selectImageState,
  (image) => image.isRefreshing
);

export const selectImageCounts = createSelector(
  selectImageState,
  (image) => image.imageCounts
);

export const selectTotalImageCount = createSelector(
  selectImageCounts,
  (counts) => counts.total || 0
);

// Combined selectors
export const selectAllImages = createSelector(
  selectProfileImage,
  selectCoverImage,
  (profile, cover) => ({
    profile,
    cover
  })
);

// ==================== EXPORTS ====================

export const {
  resetImageState,
  resetUploadState,
  resetDeleteState,
  resetSummaryState,
  clearImageErrors,
  setUploadProgress,
  setImageRefreshing,
  clearProfileImage,
  clearCoverImage,
  updateImageCounts
} = providerImageSlice.actions;

export default providerImageSlice.reducer;